interface SpringConfig {
	stiffness: number
	damping: number
	mass: number
}

class SpringAnimator {
	private currentX: number = 0
	private currentWidth: number = 0
	private velocityX: number = 0
	private velocityWidth: number = 0
	private targetX: number = 0
	private targetWidth: number = 0
	private animationFrameId: number | null = null
	private readonly element: HTMLElement
	private config: SpringConfig

	private isStretching: boolean = false
	private stretchStartX: number = 0
	private stretchStartWidth: number = 0
	private stretchStartTime: number = 0
	private stretchDuration: number = 300
	private translateStart: number = 100
	private translateDuration: number = 400
	private maxStretchAmount: number = 150
	private stretchDurationMultiplier: number = 1
	private easingPower: number = 3

	constructor(element: HTMLElement, config: SpringConfig) {
		this.element = element
		this.config = config
	}

	private applySpringForce(current: number, target: number, velocity: number): { position: number, velocity: number } {
		const displacement: number = target - current
		const springForce: number = displacement * this.config.stiffness
		const dampingForce: number = -velocity * this.config.damping
		const acceleration: number = (springForce + dampingForce) / this.config.mass
		const newVelocity: number = velocity + acceleration
		const newPosition: number = current + newVelocity

		return { position: newPosition, velocity: newVelocity }
	}

	private animate = () => {
		if (this.isStretching) {
			const elapsed: number = Date.now() - this.stretchStartTime
			const effectiveStretchDuration: number = this.stretchDuration * this.stretchDurationMultiplier
			const effectiveTranslateStart: number = this.translateStart * this.stretchDurationMultiplier
			const effectiveTranslateDuration: number = this.translateDuration * this.stretchDurationMultiplier
			const totalDuration: number = Math.max(effectiveStretchDuration, effectiveTranslateStart + effectiveTranslateDuration)

			const distance: number = this.targetX - this.stretchStartX
			let newX: number = this.stretchStartX
			let newWidth: number = this.stretchStartWidth

			const easeOut = (t: number) => 1 - Math.pow(1 - t, this.easingPower)

			if (elapsed < effectiveStretchDuration) {
				const stretchProgress: number = elapsed / effectiveStretchDuration
				const easedProgress: number = easeOut(stretchProgress)
				const stretchAmount: number = Math.min(Math.abs(distance), this.maxStretchAmount)
				newWidth = this.stretchStartWidth + stretchAmount * easedProgress
			} else {
				const stretchAmount: number = Math.min(Math.abs(distance), this.maxStretchAmount)
				newWidth = this.stretchStartWidth + stretchAmount
			}

			if (elapsed >= effectiveTranslateStart) {
				const translateElapsed: number = elapsed - effectiveTranslateStart
				if (translateElapsed < effectiveTranslateDuration) {
					const translateProgress: number = translateElapsed / effectiveTranslateDuration
					const easedTranslate: number = easeOut(translateProgress)
					newX = this.stretchStartX + distance * easedTranslate

					const shrinkProgress: number = translateElapsed / effectiveTranslateDuration
					const easedShrink: number = easeOut(shrinkProgress)
					const stretchAmount: number = Math.min(Math.abs(distance), this.maxStretchAmount)
					const maxWidth: number = this.stretchStartWidth + stretchAmount
					newWidth = maxWidth - (maxWidth - this.targetWidth) * easedShrink
				} else {
					newX = this.targetX
					newWidth = this.targetWidth
				}
			}

			this.currentX = newX
			this.currentWidth = newWidth
			this.element.style.transform = `translateX(${this.currentX}px)`
			this.element.style.width = `${this.currentWidth}px`

			if (elapsed >= totalDuration) {
				this.isStretching = false
				this.currentX = this.targetX
				this.currentWidth = this.targetWidth
				this.element.style.transform = `translateX(${this.targetX}px)`
				this.element.style.width = `${this.targetWidth}px`
				this.animationFrameId = null
			} else {
				this.animationFrameId = requestAnimationFrame(this.animate)
			}
			return
		}

		if (!this.isStretching) {
			const xResult: { position: number, velocity: number } = this.applySpringForce(this.currentX, this.targetX, this.velocityX)
			const widthResult: { position: number, velocity: number } = this.applySpringForce(this.currentWidth, this.targetWidth, this.velocityWidth)

			this.currentX = xResult.position
			this.velocityX = xResult.velocity
			this.currentWidth = widthResult.position
			this.velocityWidth = widthResult.velocity

			this.element.style.transform = `translateX(${this.currentX}px)`
			this.element.style.width = `${this.currentWidth}px`

			const isSettledX: boolean = Math.abs(this.targetX - this.currentX) < 0.5 && Math.abs(this.velocityX) < 0.5
			const isSettledWidth: boolean = Math.abs(this.targetWidth - this.currentWidth) < 0.5 && Math.abs(this.velocityWidth) < 0.5

			if (!isSettledX || !isSettledWidth) {
				this.animationFrameId = requestAnimationFrame(this.animate)
			} else {
				this.currentX = this.targetX
				this.currentWidth = this.targetWidth
				this.element.style.transform = `translateX(${this.currentX}px)`
				this.element.style.width = `${this.currentWidth}px`
				this.animationFrameId = null
			}
		}
	}

	public animateTo(x: number, width: number, newConfig?: Partial<SpringConfig>, enableStretch: boolean = false, stretchMultiplier: number = 1, easingPower: number = 3) {
		if (newConfig) {
			this.config = { ...this.config, ...newConfig }
		}

		this.targetX = x
		this.targetWidth = width

		if (enableStretch) {
			this.isStretching = true
			this.stretchStartX = this.currentX
			this.stretchStartWidth = this.currentWidth
			this.stretchStartTime = Date.now()
			this.stretchDurationMultiplier = stretchMultiplier
			this.easingPower = easingPower
		}

		if (this.animationFrameId === null) {
			this.animationFrameId = requestAnimationFrame(this.animate)
		}
	}

	public snapTo(x: number, width: number) {
		this.currentX = x
		this.currentWidth = width
		this.targetX = x
		this.targetWidth = width
		this.velocityX = 0
		this.velocityWidth = 0
		this.isStretching = false
		this.element.style.transform = `translateX(${x}px)`
		this.element.style.width = `${width}px`
	}
}

$(() => {
	const $marketsContainer = $('#sectionsHeader .markets-scroll-container')
	const $scrollContainer = $marketsContainer.find('.markets-scroll')
	const $leftArrow = $marketsContainer.find('.scroll-arrow-left')
	const $rightArrow = $marketsContainer.find('.scroll-arrow-right')

	if ($scrollContainer.length === 0)
		return

	const scrollContainer: HTMLElement | undefined = $scrollContainer[0]

	const updateScrollState = () => {
		const scrollLeft: number = scrollContainer.scrollLeft
		const scrollWidth: number = scrollContainer.scrollWidth
		const clientWidth: number = scrollContainer.clientWidth
		const maxScroll: number = scrollWidth - clientWidth
		const threshold: number = 5

		if (scrollLeft <= threshold) {
			$marketsContainer.addClass('at-start')
			$leftArrow.addClass('hidden')
		} else {
			$marketsContainer.removeClass('at-start')
			$leftArrow.removeClass('hidden')
		}

		if (maxScroll - threshold <= scrollLeft) {
			$marketsContainer.addClass('at-end')
			$rightArrow.addClass('hidden')
		} else {
			$marketsContainer.removeClass('at-end')
			$rightArrow.removeClass('hidden')
		}
	}

	const scrollByAmount = (direction: 'left' | 'right') => {
		const scrollAmount: number = scrollContainer.clientWidth * 0.5
		const currentScroll: number = scrollContainer.scrollLeft
		const targetScroll: number = direction === 'left'
			? currentScroll - scrollAmount
			: currentScroll + scrollAmount

		scrollContainer.scrollTo({
			left: targetScroll,
			behavior: 'smooth'
		})
	}

	$leftArrow.on('click', () => scrollByAmount('left'))
	$rightArrow.on('click', () => scrollByAmount('right'))
	$scrollContainer.on('scroll', updateScrollState)

	// preview pill "Coming Soon" tooltip (Bootstrap)
	const bs = (window as any).bootstrap
	$scrollContainer.find('.market-button[data-preview]').each(function () {
		new bs.Tooltip(this, { trigger: 'hover' })
	})

	setTimeout(updateScrollState, 100)

	$marketsContainer.find('.market-button').on('click', function () {
		const $destinationsContainer = $('#destinations-container')
		if ($destinationsContainer.length === 0)
			return

		const offsetTop: number = $destinationsContainer.offset()?.top ?? 0
		const headerHeight: number = $('#sectionsHeader').outerHeight() ?? 0
		const scrollPosition: number = offsetTop - headerHeight - 80

		window.scrollTo({
			top: scrollPosition,
			behavior: 'smooth'
		})
	})

	const $sectionsHeader = $('#sectionsHeader')
	if ($sectionsHeader.length === 0)
		return

	const updateVisibility = () => {
		const $main: JQuery<HTMLElement> = $('main')
		if ($main.length === 0) {
			$sectionsHeader.removeClass('is-visible').addClass('is-hidden')
			return
		}

		const $h1: JQuery<HTMLElement> = $main.find('h1')
		if ($h1.length === 0) {
			$sectionsHeader.removeClass('is-visible').addClass('is-hidden')
			return
		}

		const scrollTop: number = $(window).scrollTop() ?? 0
		const h1Top: number = $h1.offset()?.top ?? 0
		const isH1AtTop: boolean = h1Top <= scrollTop

		if (isH1AtTop) {
			$sectionsHeader.addClass('is-visible').removeClass('is-hidden')
		} else {
			$sectionsHeader.removeClass('is-visible').addClass('is-hidden')
		}
	}

	$(window).on('scroll load resize', updateVisibility)
	setTimeout(updateVisibility, 100)

	const $indicator = $('#sectionsHeader .indicator-line')
	if ($indicator.length === 0)
		return

	const indicatorElement: HTMLElement | undefined = $indicator[0]
	const normalConfig: SpringConfig = { stiffness: 0.15, damping: 0.7, mass: 1 }
	const extremeConfig: SpringConfig = { stiffness: 0.08, damping: 0.6, mass: 1.2 }

	const animator = new SpringAnimator(indicatorElement, normalConfig)

	const $whereToTitle = $('#sectionsHeader .sections-title[data-indicator-target="where-to"]')
	const $sectionButtons = $('#sectionsHeader .section-link[data-indicator-target]')
	let activeTarget: string | null = null
	let lastClickTime: number = 0
	const clickCooldown: number = 1200

	const updateIndicatorPosition = (targetElement: HTMLElement, targetId: string) => {
		const headerContent: HTMLElement | undefined = $('#sectionsHeader .sections-header-content')[0]
		if (!headerContent)
			return

		const contentRect: DOMRect = headerContent.getBoundingClientRect()
		const targetRect: DOMRect = targetElement.getBoundingClientRect()

		const x: number = targetRect.left - contentRect.left
		const width: number = targetRect.width

		const previousTarget: string | null = activeTarget
		activeTarget = targetId

		const isWhereToTransition: boolean = previousTarget === 'where-to' || targetId === 'where-to'
		const config: SpringConfig = isWhereToTransition ? extremeConfig : normalConfig
		const leavingWhereToStretch: boolean = previousTarget === 'where-to' && targetId !== 'where-to'
		const stretchMultiplier: number = leavingWhereToStretch ? 2 : 1
		const easingPower: number = leavingWhereToStretch ? 6 : 3

		animator.animateTo(x, width, config, leavingWhereToStretch, stretchMultiplier, easingPower)

		$whereToTitle.removeClass('active')
		$sectionButtons.removeClass('active')
		$(targetElement).addClass('active')
	}

	const checkScrollPosition = () => {
		const now: number = Date.now()
		if (now - lastClickTime < clickCooldown)
			return

		const scrollTop: number = $(window).scrollTop() ?? 0
		const headerHeight: number = $('#sectionsHeader').outerHeight() ?? 0
		const offset: number = headerHeight + 100

		let closestSection: { element: HTMLElement, id: string, distance: number } | null = null

		$sectionButtons.each(function () {
			const targetId: string | undefined = $(this).attr('data-target')
			const sectionId: string | undefined = $(this).attr('data-indicator-target')
			if (!targetId || !sectionId)
				return

			const $section: JQuery<HTMLElement> = $(`#${targetId}`)
			if ($section.length === 0)
				return

			const sectionTop: number = $section.offset()?.top ?? 0
			const sectionBottom: number = sectionTop + ($section.outerHeight() ?? 0)
			const viewportTop: number = scrollTop + offset

			if (viewportTop >= sectionTop && viewportTop < sectionBottom) {
				closestSection = { element: this, id: sectionId, distance: 0 }
				return false
			}

			const distanceToTop: number = Math.abs(viewportTop - sectionTop)
			if (!closestSection || distanceToTop < closestSection.distance) {
				closestSection = { element: this, id: sectionId, distance: distanceToTop }
			}
		})

		if (closestSection && closestSection.distance < 300) {
			if (activeTarget !== closestSection.id) {
				updateIndicatorPosition(closestSection.element, closestSection.id)
			}
		} else {
			const $destinationsContainer: JQuery<HTMLElement> = $('#destinations-container')
			if ($destinationsContainer.length > 0) {
				const containerTop: number = $destinationsContainer.offset()?.top ?? 0
				const containerBottom: number = containerTop + ($destinationsContainer.outerHeight() ?? 0)
				const viewportTop: number = scrollTop + offset

				if (viewportTop >= containerTop && viewportTop < containerBottom) {
					if ($whereToTitle.length > 0 && activeTarget !== 'where-to') {
						updateIndicatorPosition($whereToTitle[0], 'where-to')
					}
				}
			}
		}
	}

	const initializeIndicator = () => {
		if ($whereToTitle.length > 0) {
			const headerContent: HTMLElement | undefined = $('#sectionsHeader .sections-header-content')[0]
			if (!headerContent)
				return

			const contentRect: DOMRect = headerContent.getBoundingClientRect()
			const targetRect: DOMRect = $whereToTitle[0].getBoundingClientRect()

			const x: number = targetRect.left - contentRect.left
			const width: number = targetRect.width

			animator.snapTo(x, width)
			activeTarget = 'where-to'
			$whereToTitle.addClass('active')
		}
	}

	let scrollRafId: number | null = null
	$(window).on('scroll', () => {
		if (scrollRafId)
			cancelAnimationFrame(scrollRafId)

		scrollRafId = requestAnimationFrame(checkScrollPosition)
	})

	$whereToTitle.on('click', function () {
		lastClickTime = Date.now()

		const $destinationsContainer: JQuery<HTMLElement> = $('#destinations-container')
		if ($destinationsContainer.length === 0)
			return

		updateIndicatorPosition(this, 'where-to')

		const offsetTop: number = $destinationsContainer.offset()?.top ?? 0
		const headerHeight: number = $('#sectionsHeader').outerHeight() ?? 0
		const scrollPosition: number = offsetTop - headerHeight - 80

		window.scrollTo({
			top: scrollPosition,
			behavior: 'smooth'
		})
	})

	const $sectionLinks = $('#sectionsHeader .section-link')
	$sectionLinks.on('click', function () {
		lastClickTime = Date.now()

		const targetId: string | undefined = $(this).attr('data-target')
		const sectionId: string | undefined = $(this).attr('data-indicator-target')
		if (!targetId || !sectionId)
			return

		const $target: JQuery<HTMLElement> = $(`#${targetId}`)
		if ($target.length === 0)
			return

		updateIndicatorPosition(this, sectionId)

		const offsetTop: number = $target.offset()?.top ?? 0
		const headerHeight: number = $('#sectionsHeader').outerHeight() ?? 0
		const scrollPosition: number = offsetTop - headerHeight - 80

		window.scrollTo({
			top: scrollPosition,
			behavior: 'smooth'
		})
	})

	setTimeout(() => {
		initializeIndicator()
		checkScrollPosition()
	}, 100)

	$(window).on('resize', () => {
		const $activeElement: JQuery<HTMLElement> = activeTarget === 'where-to'
			? $whereToTitle
			: $sectionButtons.filter(`[data-indicator-target="${activeTarget}"]`)

		if ($activeElement.length > 0) {
			const headerContent: HTMLElement | undefined = $('#sectionsHeader .sections-header-content')[0]
			if (!headerContent)
				return

			const contentRect: DOMRect = headerContent.getBoundingClientRect()
			const targetRect: DOMRect = $activeElement[0].getBoundingClientRect()

			const x: number = targetRect.left - contentRect.left
			const width: number = targetRect.width

			animator.snapTo(x, width)
		}
	})
})

